import { useEffect, useState } from "react";
import { getDivergenceReportFn } from "@/server/divergence";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ResponsiveActionBar } from "@/components/form/responsive-form";
import { closeFullscreenDialog } from "@/components/fullscreen-sidebar";
import { useNavigate } from "@tanstack/react-router";

export function DivergenceReportDetails({ id }: { id: number }) {
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useEffect(() => {
    setLoading(true);
    getDivergenceReportFn({ data: { id: String(id) } })
      .then((res) => setReport(res ?? null))
      .finally(() => setLoading(false));
  }, [id]);

  const DataField = ({
    label,
    value,
    isTextArea = false,
    isPreformatted = false,
  }: {
    label: string;
    value: string;
    isTextArea?: boolean;
    isPreformatted?: boolean;
  }) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      {isTextArea ? (
        <div className="min-h-20 w-full rounded-md border border-input  px-3 py-2 text-sm">
          {isPreformatted ? (
            <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed">{value || "—"}</pre>
          ) : (
            value || "—"
          )}
        </div>
      ) : (
        <div className="w-full rounded-md border border-input  px-3 py-2 text-sm">{value || "—"}</div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <h2 className="text-lg font-semibold mb-4 p-4">Divergence Report Details</h2>
        <div className="flex-1 overflow-y-auto p-4" style={{ height: "calc(100vh - 107px)" }}>
          <Card className="mb-4">
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent className="space-y-5">
              {Array.from({ length: 11 }).map((_, index) => (
                <div key={index} className="space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex flex-col h-full">
        <h2 className="text-lg font-semibold mb-4 p-4">Divergence Report Details</h2>
        <div className="flex-1 overflow-y-auto p-4" style={{ height: "calc(100vh - 107px)" }}>
          <Card className="mb-4">
            <CardContent className="flex items-center justify-center py-8">
              <p className="text-center text-muted-foreground">No divergence report found.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <h2 className="text-lg font-semibold mb-4 p-4">Divergence Report Details</h2>
      <div className="flex-1 overflow-y-auto p-4" style={{ height: "calc(100vh - 107px)" }}>
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>Report Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <DataField label="ID" value={report?.id != null ? String(report.id) : ""} />
            <DataField label="Username" value={report?.username ?? ""} />
            <DataField label="User ID" value={report?.userId != null ? String(report.userId) : ""} />
            <DataField label="Service" value={report?.service ?? ""} />
            <DataField label="Call ID" value={report?.callId ?? ""} />
            <DataField label="Caller" value={report?.caller ?? ""} />
            <DataField label="Created" value={report?.created ? new Date(report.created).toLocaleString() : ""} />
          </CardContent>
        </Card>

        <Card className="mb-4">
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <DataField label="Reason" value={report?.reason ?? ""} isTextArea />
            <DataField label="Message" value={report?.message ?? ""} isTextArea />
            <DataField
              label="Call Info"
              value={report?.callInfo?.replace(/^\n+/, "") ?? ""}
              isTextArea
              isPreformatted
            />
          </CardContent>
        </Card>
      </div>
      <ResponsiveActionBar>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            closeFullscreenDialog();
            navigate({
              to: "/divergences",
            });
          }}
        >
          Cancel
        </Button>
      </ResponsiveActionBar>
    </div>
  );
}
